import { ChangeDetectionStrategy, Component, EventEmitter, input, Output, signal } from '@angular/core';
import { DialogFocusDirective } from './dialog-focus.directive';
import { IconComponent } from './icon.component';

import { checkFile } from '@sharedlib/sheets-editor/import-limits';
import { SHEETS_STRINGS } from '@sharedlib/sheets-editor/sheets.strings';

/**
 * Import spreadsheet — Figma frame 12 (`12_Import_Workbook_Dialog`).
 *
 * 540x522 dialog with a 492x209 drop zone. The limits paragraph is the frame's
 * own wording, and the limits it quotes are enforced in `validate()` below —
 * file-formats.md requires oversize input to "fail visibly rather than silently
 * truncating data".
 *
 * One deliberate divergence: the frame lists "<vendor> .json". AM-34 removed the
 * vendor's name from the download menu and G-4 bars vendor branding in the UI, so
 * it reads "Stitch .json" here. Flagged to Wook alongside the AM-34 question.
 */
@Component({
  selector: 'app-import-dialog',
  standalone: true,
  imports: [IconComponent, DialogFocusDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './import-dialog.component.html',
  styleUrls: ['./import-dialog.component.less', './sheets-buttons.css'],
})
export class ImportDialogComponent {
  /** NFR-8: the panel's words, from sheets.strings.ts. */
  protected readonly S = SHEETS_STRINGS;
  @Output() public readonly chosen = new EventEmitter<File>();
  @Output() public readonly cancelled = new EventEmitter<void>();
  /**
   * The chosen file is being imported (gap rows 47 and 72). The dialog stays open until the workbook exists,
   * as the prototype's does: "Choose file" reads "Importing workbook…" and, with Cancel, is disabled, a status
   * says the same, and nothing closes it or takes a second file until the host clears this.
   */
  public readonly busy = input(false);

  protected readonly dragging = signal(false);
  protected readonly error = signal<string | null>(null);

  protected cancel(): void {
    if (!this.busy()) this.cancelled.emit();
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.busy()) this.dragging.set(true);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file && !this.busy()) this.accept(file);
  }

  protected browse(): void {
    if (this.busy()) return;
    const input = document.createElement('input');
    input.type = 'file';
    // AM-41: no `.json` — JSON is Stitchy's format, never offered to a user and never labelled
    // with the engine's name. `checkFile` rejects it too, so a dragged one is refused as well.
    input.accept = '.xlsx,.xls,.ods,.csv,.tsv';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) this.accept(file);
    };
    input.click();
  }

  /** Fails visibly. Silent truncation is what file-formats.md rules out. */
  private accept(file: File): void {
    const problem = checkFile(file.name, file.size);
    if (problem) {
      this.error.set(problem);
      return;
    }
    this.error.set(null);
    this.chosen.emit(file);
  }
}
